import ModalController from "./modal_controller";

export default class extends ModalController {
  static targets = ["modalBox"];
  static values = {
    frameTarget: String,
    sizePresets: Object,
  };

  declare readonly modalBoxTarget: HTMLElement;
  declare readonly sizePresetsValue: Record<string, string>;

  /**
   * Configure the modal with a specific size
   * @param size - Size preset key (xs, sm, md, lg, xl, full, fullscreen) or custom classes
   */
  configure(size: string = "lg") {
    const sizeClasses = this.sizePresetsValue[size];

    // Remove all existing size classes
    this.clearSizeClasses();

    // Add new size classes
    const classArray = sizeClasses.split(/\s+/).filter(cls => cls.length > 0);
    this.modalBoxTarget.classList.add(...classArray);
  }

  configureWithEvent(event: Event) {
    // Use currentTarget (the element with the event listener) first
    const currentTarget = event?.currentTarget as HTMLElement;
    const eventTarget = event?.target as HTMLElement;
    
    // Look for data-size on currentTarget first, then traverse up from eventTarget
    let dataSize = currentTarget?.dataset?.size;
    
    if (!dataSize) {
      // If not found on currentTarget, traverse up from eventTarget to find it
      let element = eventTarget;
      while (element && element !== currentTarget) {
        if (element.dataset?.size) {
          dataSize = element.dataset.size;
          break;
        }
        element = element.parentElement as HTMLElement;
      }
    }

    this.configure(dataSize);
  }

  /**
   * Clear all size-related classes from the modal box
   */
  private clearSizeClasses() {
    const allSizeClasses = Object.values(this.sizePresetsValue)
      .join(" ")
      .split(/\s+/)
      .filter(cls => cls.length > 0);

    // Also include common size-related classes that might be applied
    const commonSizeClasses = [
      "w-80", "w-96", "w-full", "w-screen",
      "max-w-sm", "max-w-md", "max-w-lg", "max-w-xl", "max-w-2xl", "max-w-3xl", "max-w-4xl", "max-w-5xl", "max-w-6xl", "max-w-7xl", "max-w-none", "max-w-[95vw]",
      "h-full", "h-auto", "h-screen", "h-[95vh]", "h-[90vh]", "h-[85vh]", "h-[80vh]", "h-[70vh]", "h-[60vh]", "h-[50vh]",
      "max-h-[50vh]", "max-h-[60vh]", "max-h-[70vh]", "max-h-[80vh]", "max-h-[85vh]", "max-h-[90vh]", "max-h-[95vh]", "max-h-none"
    ];

    const allClasses = [...allSizeClasses, ...commonSizeClasses];
    this.modalBoxTarget.classList.remove(...allClasses);
  }

  /**
   * Get current modal size configuration
   */
  getCurrentSize(): string {
    const currentClasses = Array.from(this.modalBoxTarget.classList);

    // Find which preset matches the current classes
    for (const [size, classes] of Object.entries(this.sizePresetsValue)) {
      const presetClasses = classes.split(/\s+/);
      const hasAllClasses = presetClasses.every(cls => currentClasses.includes(cls));

      if (hasAllClasses) {
        return size;
      }
    }

    return "custom";
  }

  /**
   * Reset modal to default size
   */
  resetToDefault() {
    this.configure("lg");
  }

  /**
   * Closing without saving only removes `modal-open` (ModalController), leaving
   * the loaded content in the frame. Some content (e.g. the metric builder's
   * config drawer) renders a fixed `drawer-overlay` that keeps intercepting
   * clicks even while the modal is hidden — a "ghost div". Empty the frame after
   * the close transition so nothing stale survives. Saving already clears the
   * frame server-side (close_dynamic_modal), so this only matters on dismiss.
   */
  close(event: Event, preventDefault: boolean = true) {
    super.close(event, preventDefault);

    const frame = document.getElementById(
      this.frameTargetValue || "dynamic_modal_frame",
    );
    if (!frame) return;
    const closingContent = Array.from(frame.childNodes);

    window.setTimeout(() => {
      const reopened =
        this.hasElementTarget &&
        this.elementTarget.classList.contains("modal-open");
      if (!reopened) {
        closingContent.forEach(node => {
          if (node.parentNode === frame) node.remove();
        });
      }
    }, 250);
  }
}
